import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Input } from '@/shared/ui/input';
import { AppStepperForm } from '@/shared/app-stepper-form';
import { TenantApi } from '@/modules/tenants';
import type { CreateTenantRequest } from '@/api/schemas/tenants';
import type { StepDefinition, StepValidation } from '@/shared/app-stepper-form/types';
import { TenantDraftBridge } from './tenant-draft-bridge';
import type { ProvisionFormValues } from './tenant-draft-bridge';

/**
 * Tenant provisioning stepper.
 *
 * The API contract (`POST /api/v1/tenants`, register v1.4 §3.1) accepts:
 *   - slug (required): lowercase alphanumeric/hyphen, 3-63 chars
 *   - ownerAccountId (optional): an EXISTING, VERIFIED account that becomes the
 *     initial owner ATOMICALLY with the tenant. This is the preferred way to
 *     establish ownership at creation time.
 *   - displayName (optional): seeds the vendor_profile business identity
 *     (defaults to the slug when omitted)
 *   - legalName, contactName, contactEmail, contactPhone (optional): seed the
 *     vendor_profile columns / the audit row
 *   - legalEntityId (optional): UUID, nullable, semantics DECISION_BLOCKED upstream
 *
 * Everything else (verticals, locations, service areas, branding) is configured
 * AFTER creation via the tenant workspace APIs, or by a platform operator via the
 * platform-assisted onboarding surface.
 *
 * The stepper collects the provisioning payload in three steps: identity, owner,
 * and review.
 */
export function ProvisionPage(): ReactNode {
  const navigate = useNavigate();
  const [form, setForm] = useState<ProvisionFormState>({
    slug: '',
    displayName: '',
    legalName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    ownerAccountId: '',
  });
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = (field: keyof ProvisionFormState) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Filling the form from a draft the assistant proposed.
   *
   * The values land in the form's OWN state, and the ordinary form then performs the
   * ordinary write — the assistant proposes, a person confirms, and the form does the rest.
   */
  const applyDraft = (values: ProvisionFormValues): void => setForm(values);

  const steps = buildSteps(form, update, applyDraft);

  /**
   * Leaving the flow. The stepper asks for confirmation first when there are
   * unsaved edits, so this only runs once the user has agreed to lose them.
   *
   * Without this the Cancel button rendered but did nothing: the stepper treats
   * `onCancel` as optional, so a caller that forgets it gets a dead control that
   * looks live. Every caller must supply one.
   */
  const leave = (): void => {
    void navigate({ to: '/platform/tenants' });
  };

  /**
   * Whether anything would be lost by leaving.
   *
   * Derived from the form rather than tracked with a dirty flag: a flag has to
   * be set by every field and cleared by every reset, and the one field that
   * forgets to set it is the one that silently loses the user's work.
   */
  const hasUnsavedChanges = Object.values(form).some((value) => value.trim() !== '');

  return (
    <AppStepperForm
      title="Provision tenant"
      steps={steps}
      cancelLabel="Cancel"
      hasUnsavedChanges={hasUnsavedChanges}
      onCancel={leave}
      onSubmit={async () => {
        setIsSubmitting(true);
        setSubmitError(null);
        try {
          await TenantApi.create(toRequest(form));
          void navigate({ to: '/platform/tenants' });
        } catch (error) {
          setSubmitError(error instanceof Error ? error : new Error(String(error)));
        } finally {
          setIsSubmitting(false);
        }
      }}
      {...(submitError ? { submitError } : {})}
      isSubmitting={isSubmitting}
    />
  );
}

/** The form's state. The bridge owns the shape the assistant's hand-over writes. */
type ProvisionFormState = ProvisionFormValues;

type FieldUpdater = (field: keyof ProvisionFormState) => (value: string) => void;

/**
 * Maps the form to the request body.
 *
 * Empty strings are OMITTED rather than sent as `''`: the API's `.strict()`
 * schema would refuse an empty `displayName` (min length 1) and an empty
 * `contactEmail` (not an email). Omission is the correct wire form for "not
 * provided".
 */
function toRequest(form: ProvisionFormState): CreateTenantRequest {
  const request: CreateTenantRequest = { slug: form.slug.trim() };
  const displayName = form.displayName.trim();
  const legalName = form.legalName.trim();
  const contactName = form.contactName.trim();
  const contactEmail = form.contactEmail.trim();
  const contactPhone = form.contactPhone.trim();
  const ownerAccountId = form.ownerAccountId.trim();

  if (displayName !== '') request.displayName = displayName;
  if (legalName !== '') request.legalName = legalName;
  if (contactName !== '') request.contactName = contactName;
  if (contactEmail !== '') request.contactEmail = contactEmail;
  if (contactPhone !== '') request.contactPhone = contactPhone;
  if (ownerAccountId !== '') request.ownerAccountId = ownerAccountId;

  return request;
}

function buildSteps(
  form: ProvisionFormState,
  update: FieldUpdater,
  applyDraft: (values: ProvisionFormValues) => void,
): ReadonlyArray<StepDefinition> {
  return [identityStep(form, update, applyDraft), ownerStep(form, update), reviewStep(form)];
}

function identityStep(
  form: ProvisionFormState,
  update: FieldUpdater,
  applyDraft: (values: ProvisionFormValues) => void,
): StepDefinition {
  return {
    id: 'identity',
    label: 'Tenant identity',
    description:
      'The slug is the tenant’s permanent identity and cannot be changed. The display name seeds the business profile.',
    required: true,
    content: (
      <div className="space-y-4">
        {/* The assistant's hand-over. It renders nothing unless a draft is waiting. */}
        <TenantDraftBridge values={form} onApply={applyDraft} />
        <Input
          label="Slug"
          hint="Lowercase letters, digits, or hyphens. 3-63 characters. Cannot start with a hyphen."
          value={form.slug}
          onChange={(event) => update('slug')(event.currentTarget.value)}
          required
          pattern="^[a-z0-9][a-z0-9-]{1,62}$"
        />
        <Input
          label="Display name"
          hint="The business name shown to customers. Defaults to the slug when omitted."
          value={form.displayName}
          onChange={(event) => update('displayName')(event.currentTarget.value)}
          optional
        />
        <Input
          label="Legal name"
          hint="The registered legal name, if different from the display name."
          value={form.legalName}
          onChange={(event) => update('legalName')(event.currentTarget.value)}
          optional
        />
      </div>
    ),
    validate: (): StepValidation => {
      const trimmed = form.slug.trim();
      if (trimmed.length < 3) return 'invalid';
      if (!/^[a-z0-9][a-z0-9-]{1,62}$/.test(trimmed)) return 'invalid';
      return 'valid';
    },
  };
}

function ownerStep(form: ProvisionFormState, update: FieldUpdater): StepDefinition {
  return {
    id: 'owner',
    label: 'Initial owner',
    description:
      'Assign an existing, verified account as the tenant’s first owner. The membership is created atomically with the tenant.',
    required: false,
    content: (
      <div className="space-y-4">
        <Input
          label="Owner account ID"
          hint="The UUID of an existing, verified account. Leave blank to create the tenant boundary only and invite an owner later."
          value={form.ownerAccountId}
          onChange={(event) => update('ownerAccountId')(event.currentTarget.value)}
          optional
        />
        <Input
          label="Contact name"
          hint="The primary contact for this tenant. Recorded on the audit row."
          value={form.contactName}
          onChange={(event) => update('contactName')(event.currentTarget.value)}
          optional
        />
        <Input
          label="Contact email"
          type="email"
          hint="The primary contact email for this tenant."
          value={form.contactEmail}
          onChange={(event) => update('contactEmail')(event.currentTarget.value)}
          optional
        />
        <Input
          label="Contact phone"
          type="tel"
          hint="The primary contact phone number for this tenant."
          value={form.contactPhone}
          onChange={(event) => update('contactPhone')(event.currentTarget.value)}
          optional
        />
      </div>
    ),
    validate: (): StepValidation => {
      const owner = form.ownerAccountId.trim();
      if (
        owner !== '' &&
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(owner)
      ) {
        return 'invalid';
      }
      const email = form.contactEmail.trim();
      if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'invalid';
      return 'valid';
    },
  };
}

function reviewStep(form: ProvisionFormState): StepDefinition {
  return {
    id: 'review',
    label: 'Review and confirm',
    description:
      'The tenant is created in ACTIVE status. Verticals, locations, service areas and branding are configured afterwards in the tenant workspace.',
    content: <ReviewContent form={form} />,
    required: true,
    validate: (): StepValidation => 'valid',
  };
}

function ReviewContent({ form }: { form: ProvisionFormState }): ReactNode {
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ReviewField label="Slug" value={form.slug.trim() || '—'} />
        <ReviewField
          label="Display name"
          value={form.displayName.trim() || form.slug.trim() || '—'}
        />
        <ReviewField label="Legal name" value={form.legalName.trim() || 'Not set'} />
        <ReviewField label="Initial owner" value={form.ownerAccountId.trim() || 'Not assigned'} />
        <ReviewField label="Contact name" value={form.contactName.trim() || 'Not set'} />
        <ReviewField label="Contact email" value={form.contactEmail.trim() || 'Not set'} />
        <ReviewField label="Contact phone" value={form.contactPhone.trim() || 'Not set'} />
        <ReviewField label="Status after creation" value="Active" />
      </dl>

      <div className="rounded-lg border border-border bg-surface-sunken p-4">
        <p className="text-sm text-content-muted">
          <strong className="text-content">Next steps:</strong> after creation, configure verticals,
          locations, service areas and branding in the tenant workspace, or invite additional
          administrators from the tenant detail page.
        </p>
      </div>
    </div>
  );
}

function ReviewField({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-content-muted uppercase">{label}</dt>
      <dd className="text-sm text-content">{value}</dd>
    </div>
  );
}
